<template>
  <v-container fluid class="metricas-petmed">
    <div class="d-flex align-start justify-space-between flex-wrap header-row">
      <div>
        <h2 class="page-title mb-1">Métricas PetMed</h2>
        <p class="page-desc mb-0">Atenciones en clínicas Droopy: uso, costo y pacientes.</p>
      </div>

      <v-card outlined class="total-badge text-right">
        <span class="pw-label">Total de atenciones</span>
        <div class="total-badge-value">{{ loadingInsights || insightsError ? '—' : totals.attendances }}</div>
        <div v-if="historicalLabel" class="pw-pct">{{ historicalLabel }}</div>
      </v-card>
    </div>

    <PetmedFiltersBar
      v-model="filters"
      :clinics="clinics"
      :loading-clinics="loadingClinics"
      :clinics-error="clinicsError"
      :show-clinics="canSeeClinics"
      class="mt-4 mb-6"
    />

    <!-- Costo Pawer -->
    <template v-if="canSeeCosts">
      <span class="pw-section d-block mb-2">Costo Pawer</span>
      <v-row class="mb-4">
        <v-col v-for="card in costCards" :key="card.key" cols="12" sm="6" md="3">
          <v-card
            outlined
            class="kpi-card pa-4"
            :class="{ 'kpi-card--clickable': card.trendMetric && canOpenTrend }"
            @click="card.trendMetric && canOpenTrend && openTrendDialog(card.trendMetric)"
          >
            <span class="pw-label">{{ card.label }}</span>
            <div v-if="loadingInsights" class="py-2">
              <v-progress-circular indeterminate size="20" width="2" color="primary" />
            </div>
            <template v-else>
              <div class="pw-kpi" :class="card.valueClass">{{ card.value }}</div>
              <template v-if="card.trendMetric">
                <RefundSparkline v-if="!trendError" :data="trendSeries[card.trendMetric]" :color="sparkColor" />
                <div v-else class="trend-error pw-pct mt-1">
                  <v-icon small class="mr-1 trend-error">mdi-chart-line-variant</v-icon>No se pudo cargar la tendencia
                </div>
              </template>
            </template>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Uso -->
    <span class="pw-section d-block mb-2">Uso</span>
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card
          outlined
          class="kpi-card pa-4"
          :class="{ 'kpi-card--clickable': canOpenTrend }"
          @click="canOpenTrend && openTrendDialog('attendances')"
        >
          <span class="pw-label">Atenciones</span>
          <div v-if="loadingInsights" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <template v-else>
            <div class="pw-kpi">{{ insightsError ? '—' : totals.attendances }}</div>
            <RefundSparkline v-if="!trendError" :data="trendSeries.attendances" :color="sparkColor" />
            <div v-else class="trend-error pw-pct mt-1">
              <v-icon small class="mr-1 trend-error">mdi-chart-line-variant</v-icon>No se pudo cargar la tendencia
            </div>
          </template>
        </v-card>
      </v-col>
      <v-col v-if="canSeeCosts" cols="12" sm="6" md="3">
        <v-card outlined class="kpi-card pa-4">
          <span class="pw-label">Ticket promedio</span>
          <div v-if="loadingInsights" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <div v-else class="pw-kpi">{{ avgTicketLabel }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="kpi-card pa-4">
          <div v-if="loadingInsights" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <div v-else class="d-flex pair">
            <div>
              <span class="pw-label">Mascotas únicas</span>
              <div class="pw-kpi">{{ insightsError ? '—' : totals.uniquePets }}</div>
            </div>
            <div>
              <span class="pw-label">Tutores únicos</span>
              <div class="pw-kpi">{{ insightsError ? '—' : totals.uniqueTutors }}</div>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="kpi-card pa-4">
          <span class="pw-label">Mascotas recurrentes</span>
          <div v-if="loadingInsights" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <template v-else>
            <div class="pw-kpi">{{ insightsError ? '—' : totals.recurringPets }}</div>
            <span class="pw-pct">{{ recurringPctLabel }} de las mascotas únicas</span>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-4">
      <v-col cols="12">
        <PetmedBusinessLineBreakdown
          :by-business-line="insights.byBusinessLine"
          :attendances="totals.attendances"
          :loading="loadingInsights"
          :error="insightsError"
        />
      </v-col>
    </v-row>

    <PetmedByClinicTable
      v-if="canSeeClinics"
      :items="insights.byClinic"
      :show-costs="canSeeCosts"
      :can-open-detail="canSeeDetail"
      :loading="loadingInsights"
      :error="insightsError"
      class="mb-6"
      @select-clinic="openClinicAttendances"
    />

    <PetmedClinicAttendancesDialog
      v-if="canSeeDetail"
      v-model="clinicAttendancesOpen"
      :clinic="selectedClinic"
      :from-date="filters.fromDate"
      :to-date="filters.toDate"
      :show-costs="canSeeCosts"
    />

    <v-row>
      <v-col cols="12" md="8">
        <PetmedServicesChart
          :by-pawer-service="insights.byPawerService"
          :by-clinic-service="insights.byClinicService"
          :show-costs="canSeeCosts"
          :show-clinic-services="canSeeClinics"
          :loading="loadingInsights"
          :error="insightsError"
        />
      </v-col>
      <v-col cols="12" md="4">
        <PetmedPetsBreakdown
          :by-species="insights.bySpecies"
          :loading="loadingInsights"
          :error="insightsError"
        />
      </v-col>
    </v-row>

    <RefundTrendDialog
      v-model="trendDialogOpen"
      :title="trendDialogTitle"
      :points="trendDialogPoints"
      :color="sparkColor"
      :granularity="trendGranularity"
    />

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { utilsMixin } from '@/mixins/utilsMixin';
import ClinicsService from '@/services/clinics.service';
import PetmedMetricsService from '@/services/petmed-metrics.service';
import PetmedFiltersBar from '@/components/PetmedFiltersBar.vue';
import PetmedByClinicTable from '@/components/PetmedByClinicTable.vue';
import PetmedClinicAttendancesDialog from '@/components/PetmedClinicAttendancesDialog.vue';
import PetmedBusinessLineBreakdown from '@/components/PetmedBusinessLineBreakdown.vue';
import PetmedServicesChart from '@/components/PetmedServicesChart.vue';
import PetmedPetsBreakdown from '@/components/PetmedPetsBreakdown.vue';
import RefundSparkline from '@/components/RefundSparkline.vue';
import RefundTrendDialog from '@/components/RefundTrendDialog.vue';
import { can } from '@/utils/permissions';
import {
  EMPTY_INSIGHTS,
  granularityFor,
  presetRange,
  rangeLabel,
  historicalAttendancesLabel,
  pct,
} from '@/utils/petmed-metrics.utils';

// El sparkline y el diálogo pintan en SVG/canvas: se repite el valor de
// $pw-primary de src/styles/variables.scss.
const SPARK_COLOR = '#104B94';

const TREND_METRICS = {
  attendances: 'Atenciones',
  pawerCoverage: 'Cobertura comprometida',
};

const clp = (value) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);

export default {
  name: 'MetricasPetmed',
  components: {
    PetmedFiltersBar,
    PetmedByClinicTable,
    PetmedClinicAttendancesDialog,
    PetmedBusinessLineBreakdown,
    PetmedServicesChart,
    PetmedPetsBreakdown,
    RefundSparkline,
    RefundTrendDialog,
  },
  mixins: [utilsMixin],

  data() {
    return {
      filters: { ...presetRange('currentMonth'), clinicIds: [], preset: 'currentMonth' },
      insights: EMPTY_INSIGHTS,
      trend: [],
      loadingInsights: false,
      loadingTrend: false,
      insightsError: false,
      trendError: false,
      clinics: [],
      loadingClinics: false,
      clinicsError: false,
      insightsRequestId: 0,
      trendRequestId: 0,
      trendDialogOpen: false,
      trendDialogMetric: 'attendances',
      clinicAttendancesOpen: false,
      selectedClinic: null,
      sparkColor: SPARK_COLOR,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
      // Sin `view-costs` los montos llegan en null; sin `view-clinics` solo hay cifras generales.
      canSeeCosts: can('petmed-metrics:view-costs'),
      canSeeClinics: can('petmed-metrics:view-clinics'),
      // Abrir el detalle de atenciones de una clínica exige `view-detail`.
      canSeeDetail: can('petmed-metrics:view-detail'),
    };
  },

  computed: {
    totals() {
      return this.insights.totals;
    },
    queryParams() {
      return {
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate,
        clinicIds: this.filters.clinicIds,
      };
    },
    trendGranularity() {
      return granularityFor(this.filters.fromDate, this.filters.toDate);
    },
    // Total histórico global bajo la cifra del período (025). Sin el campo (backend viejo)
    // queda el rango del preset Histórico, como antes.
    historicalLabel() {
      if (this.loadingInsights || this.insightsError) return '';
      const count = this.totals.historicalAttendances;
      if (Number.isFinite(count)) {
        return historicalAttendancesLabel(count, this.insights.range, !this.filters.fromDate);
      }
      return this.filters.fromDate ? '' : rangeLabel(this.insights.range);
    },
    costCards() {
      const t = this.totals;
      const money = (v) => (this.insightsError ? '—' : clp(v));
      return [
        { key: 'coverage', label: 'Cobertura comprometida', value: money(t.pawerCoverage), trendMetric: 'pawerCoverage' },
        { key: 'paid', label: 'Pagado a clínicas', value: money(t.paidToClinics) },
        {
          key: 'pending',
          label: 'Pendiente de pago',
          value: money(t.pendingToClinics),
          valueClass: { 'kpi-pending': !this.insightsError && t.pendingToClinics > 0 },
        },
        { key: 'coverageRate', label: '% cobertura Pawer', value: pct(this.insightsError ? null : t.coverageRate) },
      ];
    },
    avgTicketLabel() {
      const v = this.totals.avgTicket;
      return this.insightsError || v == null ? '—' : clp(v);
    },
    recurringPctLabel() {
      const { recurringPets, uniquePets } = this.totals;
      if (this.insightsError || !uniquePets) return '—';
      return pct((recurringPets / uniquePets) * 100);
    },
    trendSeries() {
      return {
        attendances: this.trend.map((t) => t.attendances),
        pawerCoverage: this.trend.map((t) => t.pawerCoverage),
      };
    },
    canOpenTrend() {
      return !this.trendError && this.trend.length > 1;
    },
    trendDialogTitle() {
      return TREND_METRICS[this.trendDialogMetric];
    },
    trendDialogPoints() {
      const field = this.trendDialogMetric;
      return this.trend.map((t) => ({ period: t.bucket, value: t[field] ?? 0 }));
    },
  },

  watch: {
    queryParams: {
      deep: true,
      handler() {
        this.fetchInsights();
        this.fetchTrend();
      },
    },
  },

  created() {
    if (this.canSeeClinics) this.fetchClinics();
    this.fetchInsights();
    this.fetchTrend();
  },

  methods: {
    async fetchClinics() {
      this.loadingClinics = true;
      const res = await ClinicsService.getDroopyClinics({ excludeDemo: true });
      if (res.status === 'ok') {
        this.clinics = res.data || [];
        this.clinicsError = false;
      } else {
        this.clinicsError = true;
        this.showErrorSnackbar('No se pudieron cargar las clínicas Droopy.');
      }
      this.loadingClinics = false;
    },

    // Cada fetch toma un id; si llega una respuesta de un filtro anterior, se descarta.
    async fetchInsights() {
      const requestId = ++this.insightsRequestId;
      this.loadingInsights = true;
      const res = await PetmedMetricsService.getInsights(this.queryParams);
      if (requestId !== this.insightsRequestId) return;
      if (res.status === 'ok') {
        this.insights = { ...EMPTY_INSIGHTS, ...res.data };
        this.insightsError = false;
      } else {
        this.insights = EMPTY_INSIGHTS;
        this.insightsError = true;
        this.showErrorSnackbar('No se pudieron cargar las métricas PetMed.');
      }
      this.loadingInsights = false;
    },

    async fetchTrend() {
      const requestId = ++this.trendRequestId;
      this.loadingTrend = true;
      const res = await PetmedMetricsService.getTrend({
        ...this.queryParams,
        granularity: this.trendGranularity,
      });
      if (requestId !== this.trendRequestId) return;
      if (res.status === 'ok') {
        this.trend = res.data || [];
        this.trendError = false;
      } else {
        this.trend = [];
        this.trendError = true;
      }
      this.loadingTrend = false;
    },

    openTrendDialog(metric) {
      this.trendDialogMetric = metric;
      this.trendDialogOpen = true;
    },

    openClinicAttendances(clinic) {
      this.selectedClinic = clinic;
      this.clinicAttendancesOpen = true;
    },
  },
};
</script>

<style scoped>
.header-row {
  gap: 16px;
}
.page-title {
  color: var(--pw-primary);
  font-weight: 700;
}
.page-desc {
  font-size: 13px;
  color: var(--pw-text-muted);
}
.total-badge {
  border-color: var(--pw-primary);
  padding: 16px 26px;
  min-width: 220px;
}
.total-badge-value {
  font-size: 34px;
  font-weight: 700;
  color: var(--pw-primary);
  line-height: 1.3;
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
}
.kpi-card {
  height: 100%;
}
.kpi-card--clickable {
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}
.kpi-card--clickable:hover {
  box-shadow: var(--pw-shadow-md) !important;
}
.kpi-pending {
  color: var(--pw-warning);
}
.pair {
  gap: 24px;
}
.trend-error {
  color: var(--pw-text-faint);
}
</style>
